import platform from 'platform';
import currentDevice from 'current-device';
import Cookies from 'js-cookie';
import { API_ROOT } from 'agents';
import { callApi, generateId, statusCodes } from './utils';
import { str_slugify } from 'helpers';
import { commonStore } from 'stores/common';
import { leanplumNotFound } from 'stores/leanplum';
import get from 'lodash/get';
import qs from 'query-string';

const config = {
    baseUrl: API_ROOT,
    cookie: {
        domain: '.yousician.com',
        secure: false,
        path: '/',
    },
    channel: 'profile-pages',
    visitIdCookieName: 'ys_visit',
    visitorIdCookieName: 'ys_visitor',
    visitTtl: 30, // half an hour
    visitorTtl: 2 * 365 * 24 * 60, // 2 years
};

class Analytics {
    static initialized = false;

    constructor() {
        if (Analytics.initialized) {
            console.warn('Analytics: already initialized');
            return;
        }

        Analytics.initialized = true;

        if (!this.visitId) {
            this._setCookie(
                config.visitIdCookieName,
                generateId(),
                config.visitTtl,
            );
        }

        if (!this.visitorId) {
            this._setCookie(
                config.visitorIdCookieName,
                generateId(),
                config.visitorTtl,
            );
        }
    }

    get profileId() {
        return get(commonStore, 'user.id');
    }

    get visitId() {
        // Fixme the value can be undefined for example: if the cookie expire while the user is still on the website
        return this._getCookie(config.visitIdCookieName);
    }

    get visitorId() {
        // Fixme the value can be undefined for example: if the cookie expire while the user is still on the website
        return this._getCookie(config.visitorIdCookieName);
    }

    _getCookie(key) {
        return Cookies.get(key);
    }

    _setCookie(key, value, ttl) {
        let options = config.cookie;

        if (ttl) {
            const date = new Date();
            options = {
                ...options,
                expires: new Date(
                    date.setTime(date.getTime() + ttl * 60 * 1000),
                ),
            };
        }

        return Cookies.set(key, value, options);
    }

    /**
     * Returns the current device type. It will only
     * detect Desktop, Tablet and Phones.
     */
    get deviceType() {
        return currentDevice.type;
    }

    get referrer() {
        return window.document.referrer;
    }

    _removeCookie(key) {
        return Cookies.remove(key, config.cookie);
    }

    _getQueryParameters() {
        return qs.parse(window.location.search);
    }

    setRedirectedFrom(from) {
        this.redirectedFrom = from;
    }

    /**
     * Returns all the information about
     * the current platform.
     */
    _getPlatformProfile() {
        return platform;
    }

    _getCurrentUrl() {
        return window.location.href;
    }

    _getPageTitle() {
        return document.title;
    }

    /**
     * Returns the list of parameters that will
     * always be present regardless of
     * the event type.
     */
    _trackingDefaultParams() {
        const params = this._getQueryParameters();
        const platform = this._getPlatformProfile();

        return {
            path: window.location.pathname,
            visit_token: this.visitId,
            profile_id: this.profileId,
            visitor_token: this.visitorId,
            referring_url: this.referrer,
            channel: config.channel,
            utm_source: params.utm_source || '',
            utm_campaign: params.utm_campaign || '',
            utm_content: params.utm_content || '',
            utm_term: params.utm_term || '',
            utm_medium: params.utm_medium || '',
            variants: leanplumNotFound()
                ? ''
                : window.Leanplum.getVariants()
                      .map((v) => v.id)
                      .join(','),
            visitor_city: '',
            visitor_region: '',
            visitor_country: '',
            visitor_browser: `${platform.name} ${platform.version}`,
            visitor_os: `${platform.os}`,
            visitor_device_type: this.deviceType,
        };
    }

    /**
     * Parameters used for legacy purposes.
     */
    _trackingLegacyPageViewParams() {
        return {
            initial_referral: window.document.referrer,
            initial_referral_domain: window.document.origin,
        };
    }

    _getStatus(status) {
        // Success if status is 2xx
        const isSuccess = status.toString().charAt(0) === '2';
        return {
            success: isSuccess,
            error_message: isSuccess ? '' : statusCodes[status],
        };
    }

    trackPageView = (pageName, status = 200) => {
        const params = {
            title: this._getPageTitle(),
            url: this._getCurrentUrl(),
            page: str_slugify(pageName.replace(/\//g, '-')),
            path: pageName,
            ...this._getStatus(status),
            ...this._trackingLegacyPageViewParams(),
        };

        if (this.redirectedFrom) {
            params.redirected_from = this.redirectedFrom;
            this.redirectedFrom = null;
        }

        callApi(
            `${config.baseUrl}/web_events/new/web_page_visited`,
            this._prepareHttpParams(params),
        );
    };

    trackEvent = (params) => {
        if (params.name) {
            params.name = params.name.toLowerCase();
        }

        if (params.context) {
            params.context = params.context.toLowerCase();
        }

        callApi(
            `${config.baseUrl}/web_events/new/web_button_clicked`,
            this._prepareHttpParams(params),
        );
    };

    trackPurchaseEvent = (params) => {
        callApi(
            `${config.baseUrl}/web_events/new/web_purchase_event`,
            this._prepareHttpParams(params),
        );
    };

    _prepareHttpParams(params) {
        return {
            data: {
                ...this._trackingDefaultParams(),
                ...params,
            },
        };
    }
}

const analytics = new Analytics();
export default analytics;
